import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---------- Anonymous session id ----------
// Persists across reloads via localStorage; sent as X-Session-Id header so the
// backend can meter Cinema Mode plays for signed-out visitors (3 plays/day).
const SID_KEY = "tc_sid";
function ensureSessionId() {
  try {
    let sid = localStorage.getItem(SID_KEY);
    if (!sid) {
      sid = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `sid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return "anon-no-storage";
  }
}

export const api = axios.create({
  baseURL: API,
  timeout: 60000,
  withCredentials: true, // send session_token cookie for auth
});

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  if (!config.headers["X-Session-Id"]) {
    config.headers["X-Session-Id"] = ensureSessionId();
  }
  return config;
});

// ---------- Snapshot / core ----------
export const fetchRegions = () => api.get("/regions").then((r) => r.data.regions);
// ---------- Config ----------
export const fetchConfig = () => {
  // Pass the visitor's selected content region as a hint so the backend
  // can route Amazon links to the matching Associates storefront (e.g.
  // amazon.ae for UAE viewers, amazon.com for US viewers).
  let params = "";
  try {
    const region = localStorage.getItem("tc_region");
    if (region && /^[A-Za-z]{2}$/.test(region)) {
      params = `?region_hint=${region.toUpperCase()}`;
    }
  } catch { /* SSR / private mode */ }
  return api.get(`/config${params}`).then((r) => r.data);
};
export const fetchRandomIconicDate = () => api.get("/iconic-dates/random").then((r) => r.data);
export const postTimeJump = (payload) => api.post("/time-jump", payload).then((r) => r.data);
export const fetchSnapshot = (id) => api.get(`/snapshot/${id}`).then((r) => r.data);
export const postNarrate = (snapshot_id, voice = "onyx", mode = "bulletin") =>
  api.post("/narrate", { snapshot_id, voice, mode }).then((r) => r.data);
export const postCompare = (payload) => api.post("/compare", payload).then((r) => r.data);
export const fetchHistoricalMedia = (snapshot_id) =>
  api.get(`/media/${snapshot_id}`).then((r) => r.data);
export const postWaitlist = (email, source = "landing") =>
  api.post("/waitlist", { email, source }).then((r) => r.data);

// ---------- Auth ----------
export const postAuthSession = (session_id) =>
  api.post("/auth/session", { session_id }).then((r) => r.data);
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
export const postLogout = () => api.post("/auth/logout").then((r) => r.data);

// ---------- Favourites ----------
export const listFavourites = () => api.get("/favourites").then((r) => r.data.favourites);
export const addFavourite = (snapshot_id) =>
  api.post("/favourites", { snapshot_id }).then((r) => r.data);
export const removeFavourite = (snapshot_id) =>
  api.delete(`/favourites/${snapshot_id}`).then((r) => r.data);

// ---------- Entitlement / paywall ----------
export const fetchEntitlement = () => api.get("/me/entitlement").then((r) => r.data);

// ---------- Stripe payments ----------
export const postCheckout = ({ lookup_key = "premium_monthly", origin_url }) =>
  api.post("/payments/checkout", { lookup_key, origin_url }).then((r) => r.data);
export const fetchPaymentStatus = (session_id) =>
  api.get(`/payments/status/${session_id}`).then((r) => r.data);
export const postPortal = (return_url) =>
  api.post("/payments/portal", { return_url }).then((r) => r.data);

// ---------- Polar payments (Merchant of Record — local methods worldwide) ----------
export const fetchPolarConfig = () =>
  api.get("/payments/polar/config").then((r) => r.data).catch(() => ({ enabled: false }));
export const postPolarCheckout = ({ origin_url }) =>
  api.post("/payments/polar/checkout", { origin_url }).then((r) => r.data);
export const postPolarPortal = (return_url) =>
  api.post("/payments/polar/portal", { return_url }).then((r) => r.data);

// ---------- Localised pricing (display-only; charged in USD equivalent) ----------
export const fetchLocalizedPricing = () => {
  // Allow ?cc=IN in the URL to preview a specific country's pricing (QA aid).
  // Otherwise, use the app-selected content region (localStorage.tc_region) as
  // a soft hint so someone browsing UAE content sees AED, not USD.
  let ccParam = "";
  try {
    const urlCc = new URLSearchParams(window.location.search).get("cc");
    if (urlCc && /^[A-Za-z]{2}$/.test(urlCc)) {
      ccParam = `?cc=${urlCc.toUpperCase()}`;
    } else {
      const region = localStorage.getItem("tc_region");
      if (region && /^[A-Za-z]{2}$/.test(region)) {
        ccParam = `?region_hint=${region.toUpperCase()}`;
      }
    }
  } catch { /* SSR / no window */ }
  return api.get(`/pricing/localized${ccParam}`).then((r) => r.data).catch(() => null);
};

// ---------- Email + magic-link auth (peer to Google OAuth) ----------
export const fetchEmailAuthConfig = () =>
  api.get("/auth/email/config").then((r) => r.data).catch(() => ({ enabled: false }));
export const requestMagicLink = ({ email, return_url }) =>
  api.post("/auth/email/request", { email, return_url }).then((r) => r.data);
export const verifyMagicLink = ({ token, email }) =>
  api.post("/auth/email/verify", { token, email }).then((r) => r.data);

// ---------- Referrals ("Gift a month · earn a month") ----------
export const fetchMyReferral = () =>
  api.get("/referrals/me").then((r) => r.data);
export const lookupReferralCode = (code) =>
  api.get(`/referrals/lookup/${encodeURIComponent(code)}`).then((r) => r.data);
export const redeemReferral = (code) =>
  api.post("/referrals/redeem", { code }).then((r) => r.data);

// ---------- Auto-advance / playlists ----------
export const fetchNextEpisode = (snapshot_id, mode = "chronological") =>
  api.get(`/next-episode`, { params: { snapshot_id, mode } }).then((r) => r.data);
export const postPrepareNextEpisode = (snapshot_id, mode = "chronological") =>
  api.post(`/next-episode/prepare`, { snapshot_id, mode }).then((r) => r.data);

// ---------- Cast (phone → nearby TV) ----------
export const postCastPair = () => api.post(`/cast/pair`).then((r) => r.data);
export const postCastLink = (code) =>
  api.post(`/cast/link`, { code }).then((r) => r.data);
export const postCastCommand = ({ code, snapshot_id, mode = "cinema" }) =>
  api.post(`/cast/command`, { code, snapshot_id, mode }).then((r) => r.data);
export const fetchCastStatus = (code) =>
  api.get(`/cast/status/${code}`).then((r) => r.data);

// ---------- Monthly Sora video reel ----------
export const fetchReelsLatest = () =>
  api.get("/reels/latest").then((r) => r.data).catch(() => null);
export const postCastAck = (code) =>
  api.post(`/cast/ack/${code}`).then((r) => r.data);
